import { type ReactNode, useEffect, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Typography from "@mui/material/Typography";

import { misc } from "app/themes/colors";
import { useDismissibleBanner } from "hooks/use-dismissible-banner.hook";

const DOT_SIZE = 10;

export interface FirstUseHintProps {
  /** useDismissibleBanner key, e.g. "suno_mentions_comment_hint_v1_". */
  storageKey: string;
  /** Signed-in user id; render the hint only once it is known. */
  userId: string;
  title: string;
  body: ReactNode;
  /** Datadog RUM action names for shown / dismissed. */
  datadogActions?: { shown?: string; dismissed?: string };
  /** Stable id for tests and the dot's data-testid. */
  testId: string;
  children: ReactNode;
}

/**
 * First-run guidance pinned to a control: a small pulsing dot at the control's top-right corner,
 * and a short hint card that opens on hovering the dot or when the control gets focus. "Got it"
 * dismisses it for good; so does the caller's own "used it" signal (any instance with the same
 * storage key dismissing, see useDismissibleBanner). Modelled on chat v2's onboarding hint
 * (features/chat/v2/onboarding/first-use-hint.component.tsx), with app-sized type (14px body),
 * copy passed in, and no dismiss-on-click: a comment box is clicked before anyone has read a hint.
 *
 * The card is a Popper, not a Popover: a Popover lays a backdrop over the page, so after merely
 * hovering the dot the next click anywhere would only close it.
 */
export default function FirstUseHint({
  storageKey,
  userId,
  title,
  body,
  datadogActions,
  testId,
  children
}: FirstUseHintProps) {
  const { visible, dismiss } = useDismissibleBanner({
    syncAcrossInstances: true,
    storageKey,
    userId,
    datadogActions
  });
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLElement | null>(null);
  // Leaving the dot for the card, or briefly overshooting, must not slam the card shut.
  const closeTimer = useRef<number | undefined>(undefined);
  const cancelClose = () => window.clearTimeout(closeTimer.current);
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setIsOpen(false), 180);
  };
  useEffect(() => cancelClose, []);

  // The wrapper stays mounted after dismissal: dropping it would change the tree's shape and
  // remount the control, and the usual dismissal (picking a teammate in the box) happens while the
  // person is typing in it.
  return (
    <Box
      ref={anchorRef}
      data-testid={`first-use-hint-${testId}`}
      sx={{ position: "relative", display: "flex", flex: 1, minWidth: 0 }}
      // Teach at the moment of use: the card opens while the control has focus, and stays until
      // "Got it" or until the feature is used (the caller's own signal, e.g. markMentionsUsed).
      // Focusing or typing does not dismiss it, so the person gets to read it.
      onFocusCapture={(event) => {
        if (
          visible &&
          !(event.target as HTMLElement).closest("[data-first-use-dot]")
        ) {
          cancelClose();
          setIsOpen(true);
        }
      }}
      onBlurCapture={scheduleClose}
      // Typing closes the card (without dismissing it), so it never sits over a list the control
      // opens above itself, such as the @ picker near the bottom of a dialog.
      onKeyDownCapture={(event) => {
        if (!(event.target as HTMLElement).closest("[data-first-use-dot]")) {
          cancelClose();
          setIsOpen(false);
        }
      }}
    >
      {children}
      {visible && (
        <Box
          component="button"
          type="button"
          data-first-use-dot=""
          aria-label={`Tip: ${title}`}
          data-testid={`first-use-dot-${testId}`}
          onMouseEnter={() => {
            cancelClose();
            setIsOpen(true);
          }}
          onMouseLeave={scheduleClose}
          onFocus={() => {
            cancelClose();
            setIsOpen(true);
          }}
          onBlur={scheduleClose}
          onClick={() => {
            cancelClose();
            setIsOpen(true);
          }}
          sx={{
            position: "absolute",
            top: -4,
            right: -4,
            width: DOT_SIZE,
            height: DOT_SIZE,
            p: 0,
            border: 0,
            borderRadius: "50%",
            bgcolor: misc.purpleText,
            cursor: "pointer",
            zIndex: 3,
            boxShadow: `0 0 0 4px ${misc.purpleText}38`,
            "@media (prefers-reduced-motion: no-preference)": {
              animation: "firstUseHintPulse 1.8s ease-in-out infinite"
            },
            "@keyframes firstUseHintPulse": {
              "0%, 100%": { boxShadow: `0 0 0 3px ${misc.purpleText}38` },
              "50%": { boxShadow: `0 0 0 6px ${misc.purpleText}1f` }
            },
            "&:focus-visible": {
              outline: `2px solid ${misc.purpleText}`,
              outlineOffset: 2
            }
          }}
        />
      )}
      <Popper
        open={visible && isOpen && !!anchorRef.current}
        anchorEl={anchorRef.current}
        placement="top-start"
        // Inside a MUI Dialog (the task modal), portal into the dialog's own container: it is
        // inside the dialog's focus trap, so the keyboard can reach "Got it", and unlike rendering
        // in place it is not clipped by the dialog's scrolling content.
        container={() =>
          anchorRef.current?.closest<HTMLElement>(".MuiDialog-container") ??
          document.body
        }
        modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <Paper
          role="dialog"
          aria-label={title}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          elevation={8}
          sx={{
            p: 2,
            width: 300,
            borderRadius: "10px",
            // A border as well as the shadow, so the card stays distinct over a white dialog.
            border: 1,
            borderColor: "divider"
          }}
        >
          <Typography variant="body1" fontWeight={700}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {body}
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={() => {
              cancelClose();
              setIsOpen(false);
              dismiss({ source: "got_it" });
            }}
            sx={{
              mt: 1,
              p: 0,
              border: 0,
              bgcolor: "transparent",
              color: misc.purpleText,
              fontWeight: 700,
              fontFamily: (theme) => theme.typography.fontFamily,
              fontSize: 14,
              cursor: "pointer",
              "&:focus-visible": {
                outline: `2px solid ${misc.purpleText}`,
                outlineOffset: 2
              }
            }}
          >
            Got it
          </Box>
        </Paper>
      </Popper>
    </Box>
  );
}
